import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Star, Unlink, Plus, Save } from "lucide-react";
import {
  getCreatorAdmin,
  updateCreator,
  linkChannelToCreator,
  setPrimaryChannel,
  deleteCreator,
} from "@/lib/admin.functions";
import { formatCompact } from "@/lib/format";

export const Route = createFileRoute("/_authenticated/admin/creators/$id")({
  staticData: { sitemap: false },
  component: AdminCreatorDetail,
});

function AdminCreatorDetail() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const getFn = useServerFn(getCreatorAdmin);
  const updateFn = useServerFn(updateCreator);
  const linkFn = useServerFn(linkChannelToCreator);
  const primaryFn = useServerFn(setPrimaryChannel);
  const deleteFn = useServerFn(deleteCreator);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "creators", id],
    queryFn: () => getFn({ data: { id } }),
  });

  const [form, setForm] = useState<any>(null);
  useEffect(() => {
    if (data?.creator) {
      setForm({
        slug: data.creator.slug ?? "",
        name: data.creator.name ?? "",
        full_name: data.creator.full_name ?? "",
        birth_date: data.creator.birth_date ?? "",
        bio: data.creator.bio ?? "",
        country: data.creator.country ?? "",
        avatar_url: data.creator.avatar_url ?? "",
      });
    }
  }, [data?.creator]);

  const saveMut = useMutation({
    mutationFn: () =>
      updateFn({
        data: {
          id,
          patch: {
            slug: form.slug || undefined,
            name: form.name || undefined,
            full_name: form.full_name || null,
            birth_date: form.birth_date || null,
            bio: form.bio || null,
            country: form.country || null,
            avatar_url: form.avatar_url || null,
          },
        },
      }),
    onSuccess: () => {
      toast.success("Saved");
      qc.invalidateQueries({ queryKey: ["admin"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const [linkChannelId, setLinkChannelId] = useState("");

  async function onLink() {
    if (!linkChannelId) return;
    try {
      await linkFn({ data: { channelId: linkChannelId, creatorId: id } });
      setLinkChannelId("");
      qc.invalidateQueries({ queryKey: ["admin"] });
    } catch (e: any) {
      toast.error(e.message);
    }
  }

  async function onUnlink(channelId: string) {
    if (!confirm("Unlink this channel from the youtuber?")) return;
    try {
      await linkFn({ data: { channelId, creatorId: null } });
      qc.invalidateQueries({ queryKey: ["admin"] });
    } catch (e: any) {
      toast.error(e.message);
    }
  }

  async function onMakePrimary(channelId: string) {
    try {
      await primaryFn({ data: { creatorId: id, channelId } });
      qc.invalidateQueries({ queryKey: ["admin"] });
    } catch (e: any) {
      toast.error(e.message);
    }
  }

  async function onDeleteCreator() {
    if (!data) return;
    if (!confirm(`Delete youtuber "${data.creator.name}"? Channels will be unlinked.`)) return;
    try {
      await deleteFn({ data: { id } });
      toast.success("Deleted");
      navigate({ to: "/admin/creators" });
    } catch (e: any) {
      toast.error(e.message);
    }
  }

  if (isLoading || !form || !data) {
    return <div className="p-8 text-mono-caps text-ink/40">Loading…</div>;
  }

  return (
    <div className="p-8 max-w-5xl">
      <Link
        to="/admin/creators"
        className="inline-flex items-center gap-1.5 text-mono-caps text-ink/50 hover:text-ink mb-4"
      >
        <ArrowLeft className="size-3.5" /> All youtubers
      </Link>

      <div className="flex items-start justify-between mb-6 gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <div className="size-16 bg-secondary rounded-full overflow-hidden flex-shrink-0">
            {form.avatar_url && <img src={form.avatar_url} alt="" className="w-full h-full object-cover" />}
          </div>
          <div className="min-w-0">
            <h1 className="text-3xl truncate">{data.creator.name}</h1>
            <p className="text-mono-caps text-ink/40 mt-1">/{data.creator.slug}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <a
            href={`/y/${data.creator.slug}`}
            target="_blank"
            rel="noreferrer"
            className="text-sm ring-hairline px-3 py-2 hover:bg-secondary"
          >
            View public
          </a>
          <button
            onClick={onDeleteCreator}
            className="text-sm text-destructive ring-hairline px-3 py-2 hover:bg-destructive/10"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Details form */}
      <section className="p-5 ring-hairline bg-paper mb-6">
        <h2 className="text-mono-caps text-ink/50 mb-4">Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Name">
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full ring-hairline bg-paper px-3 py-2"
            />
          </Field>
          <Field label="Slug (URL)">
            <input
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              className="w-full ring-hairline bg-paper px-3 py-2 font-mono text-sm"
            />
          </Field>
          <Field label="Full name">
            <input
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className="w-full ring-hairline bg-paper px-3 py-2"
            />
          </Field>
          <Field label="Birth date">
            <input
              type="date"
              value={form.birth_date ?? ""}
              onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
              className="w-full ring-hairline bg-paper px-3 py-2"
            />
          </Field>
          <Field label="Country (ISO)">
            <input
              value={form.country}
              onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })}
              maxLength={2}
              className="w-full ring-hairline bg-paper px-3 py-2 font-mono"
            />
          </Field>
          <Field label="Avatar URL">
            <input
              value={form.avatar_url}
              onChange={(e) => setForm({ ...form, avatar_url: e.target.value })}
              className="w-full ring-hairline bg-paper px-3 py-2 font-mono text-xs"
            />
          </Field>
          <div className="md:col-span-2">
            <Field label="Bio">
              <textarea
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                rows={4}
                className="w-full ring-hairline bg-paper px-3 py-2 text-sm"
              />
            </Field>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending}
            className="flex items-center gap-2 bg-ink text-paper px-4 py-2 hover:bg-amber transition-colors disabled:opacity-50"
          >
            <Save className="size-4" /> Save
          </button>
        </div>
      </section>

      {/* Channels */}
      <section className="p-5 ring-hairline bg-paper">
        <h2 className="text-mono-caps text-ink/50 mb-4">Channels ({data.channels.length})</h2>

        {data.channels.length === 0 ? (
          <p className="text-ink/50 italic mb-4">No channels linked yet.</p>
        ) : (
          <div className="divide-y divide-ink/8 mb-4">
            {data.channels.map((ch: any) => (
              <div key={ch.id} className="py-2.5 flex items-center gap-3">
                <div className="size-10 bg-secondary rounded-full overflow-hidden flex-shrink-0">
                  {ch.avatar_url && <img src={ch.avatar_url} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-serif flex items-center gap-2">
                    <Link to="/admin/youtubers/$id" params={{ id: ch.id }} className="hover:text-amber truncate">
                      {ch.name}
                    </Link>
                    {ch.is_primary_channel && (
                      <span className="text-[10px] font-mono uppercase bg-amber/15 text-amber-deep px-1.5 py-0.5">
                        Primary
                      </span>
                    )}
                  </div>
                  <div className="text-mono-caps text-ink/40 text-[10px]">
                    @{ch.handle} · {formatCompact(ch.subscriber_count)} subs · {ch.video_count ?? 0} videos
                  </div>
                </div>
                {!ch.is_primary_channel && (
                  <button
                    onClick={() => onMakePrimary(ch.id)}
                    className="text-[11px] ring-hairline px-2 py-1 hover:bg-secondary flex items-center gap-1"
                    title="Set as primary channel"
                  >
                    <Star className="size-3" /> Primary
                  </button>
                )}
                <button
                  onClick={() => onUnlink(ch.id)}
                  className="text-[11px] ring-hairline px-2 py-1 hover:bg-destructive/10 text-destructive flex items-center gap-1"
                  title="Unlink channel"
                >
                  <Unlink className="size-3" /> Unlink
                </button>
              </div>
            ))}
          </div>
        )}

        {data.availableChannels.length > 0 && (
          <div className="flex items-end gap-2 border-t border-ink/8 pt-4">
            <div className="flex-1">
              <label className="text-mono-caps text-ink/50 block mb-1">Link an existing channel</label>
              <select
                value={linkChannelId}
                onChange={(e) => setLinkChannelId(e.target.value)}
                className="w-full ring-hairline bg-paper px-3 py-2 text-sm"
              >
                <option value="">— Select a channel —</option>
                {data.availableChannels.map((ch: any) => (
                  <option key={ch.id} value={ch.id}>
                    {ch.name} (@{ch.handle})
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={onLink}
              disabled={!linkChannelId}
              className="flex items-center gap-2 bg-ink text-paper px-3 py-2 hover:bg-amber transition-colors disabled:opacity-40"
            >
              <Plus className="size-4" /> Link
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-mono-caps text-ink/50 block mb-1">{label}</span>
      {children}
    </label>
  );
}
